import { useGet } from "@albertine/api-client";
import type { MemberDataFirestoreRecord, MemberListItem } from "@albertine/types";
import { useEffect, useState } from "react";
import {
    createMembershipTileState,
    type MembershipTileState,
} from "../../../utils/members.util";
import { Flex } from "../Flex";
import { PictogramIcon } from "../Icon";
import Label from "../Label";
import Tag from "../Tag";
import Tooltip from "../Tooltip";
import "./MembershipTile.css";
import { Badge } from "@/components/ui/badge";

type Props = {
    member: Pick<MemberDataFirestoreRecord | MemberListItem, "membership">;
    onEditClick?: () => void;
};

function MembershipTile({ member, onEditClick }: Props) {
    const { data: membershipTiers = [] } = useGet("membershipTiers", {});

    const [tileState, setTileState] = useState<MembershipTileState>();

    useEffect(() => {
        setTileState(
            createMembershipTileState(member.membership, membershipTiers, new Date()),
        );
    }, [member, membershipTiers]);

    const shouldShowActions = onEditClick !== undefined;

    if (!tileState) {
        return null;
    }

    return (
        <Flex.Row justifyContent="space-between" className="membership-tile">
            <Flex.Column gap="xsmall">
                <Flex.Row gap="medium">
                    <Label.Medium.Light>{tileState.title}</Label.Medium.Light>
                    {tileState.trial && (
                        <Tag.Small.Light.N90>Trial</Tag.Small.Light.N90>
                    )}

                    {tileState.expired && <Badge variant="destructive">Expired</Badge>}
                </Flex.Row>

                {member.membership && tileState.trialExtended && (
                    <Tooltip label="Extended" placement="bottom">
                        <Flex.Row gap="medium">
                            <PictogramIcon.Refresh.Small.S90 />
                            <Label.Small.Light.N50>
                                {tileState.subtitle}
                            </Label.Small.Light.N50>
                        </Flex.Row>
                    </Tooltip>
                )}

                {!(member.membership && tileState.trialExtended) && (
                    <Flex.Row gap="medium">
                        <Label.Small.Light.N50>
                            {tileState.subtitle}
                        </Label.Small.Light.N50>
                    </Flex.Row>
                )}
            </Flex.Column>

            {shouldShowActions && (
                <div>
                    {onEditClick && !member.membership && (
                        <button
                            type="button"
                            onClick={() => {
                                onEditClick();
                            }}
                        >
                            Edit
                        </button>
                    )}
                </div>
            )}
        </Flex.Row>
    );
}

export default MembershipTile;
